import React, { useState, useMemo, useEffect } from 'react';
import useTeamCalendar from '../hooks/useTeamCalendar';

/**
 * Widget Calendrier Équipe pour le Dashboard
 * Affiche les absences de l'équipe sur une semaine
 *
 * - Chef d'équipe : Affiche uniquement les absents par défaut
 * - Autres rôles : Affiche tout le monde par défaut
 */
const TeamCalendarWidget = ({ token, userRole }) => {
  const {
    data,
    loading,
    error,
    weekNumber,
    weekDays,
    periodLabel,
    isCurrentWeek,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    refresh
  } = useTeamCalendar(token);

  const [showLegend, setShowLegend] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Détecter le changement de taille d'écran
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Par défaut : Afficher uniquement les absents pour tous les rôles
  // Seuls les Employés pourraient vouloir voir "Tous" pour savoir qui est présent
  const [showOnlyAbsent, setShowOnlyAbsent] = useState(true);

  // Toujours masquer les week-ends (pas pertinent pour les absences)
  const displayDays = useMemo(() => {
    return weekDays.filter(d => !d.isWeekend);
  }, [weekDays]);

  // Filtrer les utilisateurs selon le mode d'affichage
  const filteredUsers = useMemo(() => {
    if (!data?.users || !data?.absences) return [];

    if (!showOnlyAbsent) {
      // Afficher tout le monde
      return data.users;
    }

    // Afficher uniquement les utilisateurs qui ont au moins une absence cette semaine
    const usersWithAbsences = new Set();
    Object.values(data.absences).forEach(dayAbsences => {
      Object.keys(dayAbsences).forEach(userId => {
        usersWithAbsences.add(userId);
      });
    });

    return data.users.filter(user => usersWithAbsences.has(user.id));
  }, [data, showOnlyAbsent]);

  // Types d'absence utilisés (pour la légende)
  const usedTypes = useMemo(() => {
    if (!data?.absences || !data?.availability_types) return [];

    const usedCodes = new Set();
    Object.values(data.absences).forEach(dayAbsences => {
      Object.values(dayAbsences).forEach(absence => {
        usedCodes.add(absence.code);
      });
    });

    return data.availability_types.filter(t => usedCodes.has(t.code));
  }, [data]);

  // Compteur pour le titre
  const absentCount = filteredUsers.length;
  const totalCount = data?.users?.length || 0;

  // Styles - responsive mobile
  const styles = {
    container: {
      background: 'white',
      borderRadius: isMobile ? '8px' : '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
      padding: isMobile ? '12px' : '20px',
      marginTop: 0,  // Retiré le marginTop pour l'alignement dans la grille
      height: '100%',
      boxSizing: 'border-box'
    },
    header: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      marginBottom: isMobile ? '12px' : '16px',
      gap: isMobile ? '10px' : '12px'
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      color: '#333',
      margin: 0,
      flexWrap: 'wrap'
    },
    navigation: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: isMobile ? 'space-between' : 'flex-end',
      gap: isMobile ? '4px' : '8px',
      flexWrap: 'wrap'
    },
    navButton: {
      background: '#f0f0f0',
      border: 'none',
      borderRadius: '6px',
      padding: isMobile ? '6px 8px' : '6px 12px',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '500',
      color: '#333',
      transition: 'all 0.2s'
    },
    navButtonActive: {
      background: '#667eea',
      color: 'white'
    },
    periodLabel: {
      fontSize: isMobile ? '11px' : '14px',
      color: '#666',
      fontWeight: '500',
      textAlign: 'center'
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '4px' : '8px',
      fontSize: isMobile ? '11px' : '13px',
      color: '#666'
    },
    toggleButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: isMobile ? '4px 8px' : '4px 10px',
      borderRadius: '20px',
      border: '1px solid #ddd',
      background: 'white',
      cursor: 'pointer',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    toggleButtonActive: {
      background: '#667eea',
      color: 'white',
      borderColor: '#667eea'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: isMobile ? '16px' : '20px',
      height: isMobile ? '16px' : '20px',
      padding: '0 4px',
      borderRadius: '10px',
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: '600',
      background: '#e0e0e0',
      color: '#666'
    },
    badgeActive: {
      background: 'rgba(255,255,255,0.3)',
      color: 'white'
    },
    grid: {
      overflowX: 'auto',
      marginBottom: '12px',
      WebkitOverflowScrolling: 'touch'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? 'auto' : '600px'
    },
    th: {
      padding: isMobile ? '6px 2px' : '8px 4px',
      fontSize: isMobile ? '10px' : '12px',
      fontWeight: '600',
      textAlign: 'center',
      borderBottom: '2px solid #e0e0e0',
      color: '#666'
    },
    thWeekend: {
      background: '#f9f9f9',
      color: '#999'
    },
    thToday: {
      color: '#667eea',
      background: '#f0f4ff'
    },
    td: {
      padding: isMobile ? '3px 2px' : '4px',
      textAlign: 'center',
      borderBottom: '1px solid #f0f0f0',
      fontSize: isMobile ? '10px' : '11px',
      height: isMobile ? '28px' : '32px',
      position: 'relative'
    },
    tdName: {
      textAlign: 'left',
      paddingLeft: isMobile ? '4px' : '8px',
      fontWeight: '500',
      color: '#333',
      whiteSpace: 'nowrap',
      maxWidth: isMobile ? '80px' : '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    tdWeekend: {
      background: '#f9f9f9'
    },
    tdToday: {
      background: '#f0f4ff'
    },
    absenceCell: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '2px 4px' : '2px 6px',
      borderRadius: '4px',
      fontSize: isMobile ? '9px' : '10px',
      fontWeight: '600',
      color: 'white',
      cursor: 'pointer',
      transition: 'transform 0.15s'
    },
    summaryRow: {
      background: '#f8f9fa',
      fontWeight: '600'
    },
    summaryLabel: {
      textAlign: 'left',
      paddingLeft: isMobile ? '4px' : '8px',
      fontSize: isMobile ? '9px' : '11px',
      color: '#666'
    },
    summaryValue: {
      fontSize: isMobile ? '9px' : '12px'
    },
    summaryAbsent: {
      color: '#dc3545'
    },
    summaryPresent: {
      color: '#28a745'
    },
    legend: {
      marginTop: '12px',
      padding: isMobile ? '8px' : '12px',
      background: '#f8f9fa',
      borderRadius: '8px'
    },
    legendToggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: isMobile ? '11px' : '12px',
      color: '#666',
      padding: '4px 8px',
      borderRadius: '4px'
    },
    legendItems: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: isMobile ? '8px' : '12px',
      marginTop: '8px'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '4px' : '6px',
      fontSize: isMobile ? '10px' : '12px',
      color: '#333'
    },
    legendColor: {
      width: isMobile ? '12px' : '16px',
      height: isMobile ? '12px' : '16px',
      borderRadius: '4px'
    },
    tooltip: {
      position: 'absolute',
      background: '#333',
      color: 'white',
      padding: '6px 10px',
      borderRadius: '6px',
      fontSize: '11px',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '24px' : '40px',
      color: '#666',
      fontSize: isMobile ? '12px' : '14px'
    },
    error: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '24px' : '40px',
      color: '#dc3545',
      background: '#fff5f5',
      borderRadius: '8px',
      gap: '12px',
      fontSize: isMobile ? '12px' : '14px'
    },
    empty: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '24px' : '40px',
      color: '#666',
      fontStyle: 'italic',
      fontSize: isMobile ? '12px' : '14px'
    },
    refreshButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '4px',
      color: '#666',
      fontSize: isMobile ? '12px' : '14px'
    }
  };

  // Affichage loading
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          Chargement du calendrier...
        </div>
      </div>
    );
  }

  // Affichage erreur
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          Erreur: {error}
          <button onClick={refresh} style={{ ...styles.navButton, marginLeft: '12px' }}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Pas de données
  if (!data || !data.users || data.users.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            <span>📅</span>
            Absences équipe
          </h3>
        </div>
        <div style={styles.empty}>
          Aucun collaborateur trouvé
        </div>
      </div>
    );
  }

  const { absences, daily_summary } = data;

  return (
    <div style={styles.container}>
      {/* Header compact sur une ligne */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {/* Titre à gauche */}
        <h3 style={{ ...styles.title, marginRight: 'auto' }}>
          <span>📅</span>
          Absences équipe
          {absentCount > 0 && (
            <span style={{
              ...styles.badge,
              background: '#dc3545',
              color: 'white',
              marginLeft: '6px'
            }}>
              {absentCount}
            </span>
          )}
        </h3>

        {/* Contrôles à droite - tous sur une ligne */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
          {/* Toggle Absents / Tous */}
          <button
            onClick={() => setShowOnlyAbsent(true)}
            style={{
              ...styles.toggleButton,
              ...(showOnlyAbsent ? styles.toggleButtonActive : {}),
              padding: '4px 8px'
            }}
            title="Afficher uniquement les absents"
          >
            Absents
          </button>
          <button
            onClick={() => setShowOnlyAbsent(false)}
            style={{
              ...styles.toggleButton,
              ...(!showOnlyAbsent ? styles.toggleButtonActive : {}),
              padding: '4px 8px'
            }}
            title="Afficher tout le monde"
          >
            Tous
          </button>

          <span style={{ width: '1px', height: '16px', background: '#ddd', margin: '0 2px' }} />

          {/* Navigation semaine */}
          <button onClick={goToPreviousWeek} style={{ ...styles.navButton, padding: '4px 8px' }} title="Semaine précédente">◀</button>
          <span style={{ ...styles.periodLabel, minWidth: 'auto', whiteSpace: 'nowrap' }}>
            Sem. {weekNumber} ({periodLabel})
          </span>
          <button onClick={goToNextWeek} style={{ ...styles.navButton, padding: '4px 8px' }} title="Semaine suivante">▶</button>

          {!isCurrentWeek && (
            <button
              onClick={goToCurrentWeek}
              style={{ ...styles.navButton, ...styles.navButtonActive, padding: '4px 8px' }}
              title="Revenir à aujourd'hui"
            >
              Auj.
            </button>
          )}

          <button onClick={refresh} style={{ ...styles.refreshButton, padding: '4px' }} title="Rafraîchir">🔄</button>
        </div>
      </div>

      {/* Grille calendrier */}
      <div style={styles.grid}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, textAlign: 'left', paddingLeft: isMobile ? '4px' : '8px', width: isMobile ? '80px' : '120px' }}>
                {isMobile ? 'Nom' : 'Collaborateur'}
              </th>
              {displayDays.map(day => (
                <th
                  key={day.date}
                  style={{
                    ...styles.th,
                    ...(day.isToday ? styles.thToday : {})
                  }}
                >
                  <div>{isMobile ? day.dayName.charAt(0).toUpperCase() : day.dayName}</div>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700' }}>{day.dayNumber}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Message si aucun utilisateur */}
            {filteredUsers.length === 0 && (
              <tr>
                <td
                  colSpan={displayDays.length + 1}
                  style={{
                    ...styles.td,
                    textAlign: 'center',
                    padding: isMobile ? '16px' : '20px',
                    color: '#28a745',
                    fontStyle: 'italic',
                    fontSize: isMobile ? '11px' : '13px'
                  }}
                >
                  {showOnlyAbsent
                    ? '🎉 Aucun absent cette semaine !'
                    : 'Aucun collaborateur dans ce groupe'}
                </td>
              </tr>
            )}

            {/* Lignes utilisateurs */}
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td style={{ ...styles.td, ...styles.tdName }} title={user.name}>
                  {isMobile ? user.name.split(' ')[0] : user.name}
                </td>
                {displayDays.map(day => {
                  const absence = absences[day.date]?.[user.id];
                  const isHovered = hoveredCell?.date === day.date && hoveredCell?.userId === user.id;

                  return (
                    <td
                      key={day.date}
                      style={{
                        ...styles.td,
                        ...(day.isToday ? styles.tdToday : {})
                      }}
                    >
                      {absence ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span
                            style={{
                              ...styles.absenceCell,
                              background: absence.color,
                              transform: isHovered ? 'scale(1.1)' : 'scale(1)'
                            }}
                            onMouseEnter={() => setHoveredCell({ date: day.date, userId: user.id })}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {absence.code}
                          </span>
                          {isHovered && !isMobile && (
                            <div style={styles.tooltip}>
                              {absence.name}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Ligne de résumé */}
            <tr style={styles.summaryRow}>
              <td style={{ ...styles.td, ...styles.summaryLabel }}>
                {isMobile ? '' : 'Résumé'}
              </td>
              {displayDays.map(day => {
                const summary = daily_summary[day.date];
                if (!summary) {
                  return (
                    <td key={day.date} style={styles.td}>
                      -
                    </td>
                  );
                }
                return (
                  <td key={day.date} style={{ ...styles.td, ...(day.isToday ? styles.tdToday : {}) }}>
                    <div style={{ ...styles.summaryValue, ...styles.summaryAbsent }}>
                      {summary.absent}{isMobile ? '' : ' abs.'}
                    </div>
                    <div style={{ ...styles.summaryValue, ...styles.summaryPresent }}>
                      {summary.present}{isMobile ? '' : ' prés.'}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Légende */}
      {usedTypes.length > 0 && (
        <div>
          <button
            onClick={() => setShowLegend(!showLegend)}
            style={styles.legendToggle}
          >
            {showLegend ? '▼' : '▶'} Légende ({usedTypes.length} types)
          </button>

          {showLegend && (
            <div style={styles.legend}>
              <div style={styles.legendItems}>
                {usedTypes.map(type => (
                  <div key={type.code} style={styles.legendItem}>
                    <span style={{ ...styles.legendColor, background: type.color }} />
                    <span>{type.code}</span>
                    <span style={{ color: '#666' }}>- {type.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamCalendarWidget;
